/**
 * The shared Reply panel for every conversation inbox (spec 2026-10-01 §3).
 * The draft is written by the worker; this panel reviews, edits and sends.
 */
import { useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../api/client'
import { canSend, replySubject, threadPollMs, timeline, type Draft, type Thread } from '../lib/replies'
import { markerProblems, previewBody, type ActionChoice } from '../lib/replyActions'
import { ReplyActionCards } from './ReplyActionCards'
import { fmtRelative } from '../lib/format'
import { InboundBlock } from './InboundBlock'
import { insertAt, type Selection } from '../lib/insertText'
import { InsertContext, NO_ADDRESS } from './replyInsert'
import { apiErrorText } from '../lib/apiErrorText'

type Props = {
  channel: 'contact' | 'feedback' | 'email'
  messageId: number
  /** Rendered beside Preview and Redraft; can call useInsertIntoBody() from ./replyInsert. */
  extraTools?: ReactNode
}

const errText = apiErrorText

// Editor text survives collapsing a message (the panel unmounts); keyed by
// channel and message, valid only for the draft it was typed against.
const editorCache = new Map<string, { subject: string; body: string; draftId: number | null }>()

/** A 409 from /send is either a repeat within 10 minutes (the only case
 *  "Send anyway" applies to) or a stale draft (reload, never override). */
/** Apply-and-send errors carry structured detail: {message, reasons} on a
 *  409 (nothing changed) or {message, index, reason} on a 422 (an action failed). */
function applyDetail(err: unknown): { message?: string; reasons?: string[]; index?: number; reason?: string } | null {
  if (!(err instanceof ApiError)) return null
  const d = err.detail as { detail?: unknown } | null
  return d && typeof d.detail === 'object' && d.detail !== null ? d.detail as never : null
}

function isRepeatConflict(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false
  const d = err.detail as { detail?: unknown } | null
  return !!d && typeof d.detail === 'string' && d.detail.startsWith('A reply went to this person')
}

export function ReplyPanel({ channel, messageId, extraTools }: Props) {
  const qc = useQueryClient()
  const cacheKey = `${channel}:${messageId}`
  const key = ['reply-thread', channel, messageId]
  const thread = useQuery({
    queryKey: key,
    queryFn: () => api.get<Thread>(`/api/replies/${channel}/${messageId}`),
    refetchInterval: (q) => threadPollMs(q.state.data),
  })
  const draft: Draft | null = thread.data?.current_draft ?? null
  const usable = draft && ['ready', 'no_reply_needed', 'failed'].includes(draft.status) ? draft : null
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [loadedDraft, setLoadedDraft] = useState<number | null>(null)
  const [instruction, setInstruction] = useState('')
  const [preview, setPreview] = useState<string | null>(null)
  const [width, setWidth] = useState<393 | 760>(393)
  const [showChecks, setShowChecks] = useState(false)
  const [choices, setChoices] = useState<Record<number, ActionChoice>>({})
  const [expected, setExpected] = useState<Record<number, string>>({})
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  // Where staff last left the cursor, tagged with the draft it was in (a newly loaded draft has none).
  const selection = useRef<(Selection & { draft: number | null }) | null>(null)
  const actions = thread.data?.actions ?? []
  const previewTexts: Record<number, string> = Object.fromEntries(
    actions.map((a) => [a.index, choices[a.index]?.skip ? '' : (expected[a.index] ?? a.expected_text)]))

  // Load a new ready draft into the editor (adjusting state during render,
  // the pattern the react-hooks lint rule prefers over an effect).
  if (draft && draft.status === 'ready' && loadedDraft !== draft.id) {
    const kept = editorCache.get(cacheKey)
    const restore = kept && kept.draftId === draft.id ? kept : null
    setSubject(restore ? restore.subject : replySubject(channel, draft.subject, thread.data?.message.subject))
    setBody(restore ? restore.body : (draft.body_md ?? ''))
    setLoadedDraft(draft.id)
    setPreview(null)
    setChoices({})
    setExpected(Object.fromEntries((thread.data?.actions ?? []).map((a) => [a.index, a.expected_text])))
  }

  // No ready draft to load (none yet, failed, or no reply needed): an emailed
  // conversation still starts with "Re: <their subject>".
  const [prefilled, setPrefilled] = useState(false)
  if (!prefilled && thread.data && !(draft && draft.status === 'ready')) {
    setPrefilled(true)
    if (!subject) setSubject(replySubject(channel, null, thread.data.message.subject))
  }

  const edit = (nextSubject: string, nextBody: string) => {
    setSubject(nextSubject)
    setBody(nextBody)
    editorCache.set(cacheKey, { subject: nextSubject, body: nextBody, draftId: loadedDraft })
  }

  const insertIntoBody = (text: string) => {
    const sel = selection.current?.draft === loadedDraft ? selection.current : null
    const next = insertAt(body, text, sel)
    edit(subject, next.body)
    selection.current = { start: next.cursor, end: next.cursor, draft: loadedDraft }
    requestAnimationFrame(() => {
      bodyRef.current?.focus()
      bodyRef.current?.setSelectionRange(next.cursor, next.cursor)
    })
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: key })
    qc.invalidateQueries({ queryKey: [`${channel}-messages`] })
  }
  const redraft = useMutation({
    mutationFn: () => api.post<Draft>(`/api/replies/${channel}/${messageId}/redraft`,
      { instruction: instruction.trim() || null }),
    onSuccess: () => { setInstruction(''); refresh() },
  })
  const discard = useMutation({
    mutationFn: (reason: string) => api.post<Draft>(`/api/replies/drafts/${usable!.id}/discard`, { reason }),
    onSuccess: () => {
      editorCache.delete(cacheKey)
      setSubject(replySubject(channel, null, thread.data?.message.subject))
      setBody(''); setPreview(null); refresh()
    },
  })
  const resolve = useMutation({
    mutationFn: ({ index, resolution }: { index: number; resolution: 'done' | 'not_needed' }) =>
      api.post<Draft>(`/api/replies/drafts/${usable!.id}/actions/${index}`, { resolution }),
    onSuccess: refresh,
  })
  const showPreview = useMutation({
    mutationFn: () => api.post<{ html: string }>('/api/replies/preview',
      { channel, message_id: messageId, body_md: actions.length ? previewBody(body, previewTexts) : body }),
    onSuccess: (r) => setPreview(r.html),
  })
  const send = useMutation({
    mutationFn: (confirm_repeat: boolean) => actions.length && usable?.status === 'ready'
      ? api.post(`/api/replies/${channel}/${messageId}/apply-and-send`,
        { subject, body_md: body, draft_id: usable.id, confirm_repeat, actions: Object.values(choices) })
      : api.post(`/api/replies/${channel}/${messageId}/send`,
        { subject, body_md: body, draft_id: usable?.status === 'ready' ? usable.id : null, confirm_repeat }),
    onSuccess: () => {
      editorCache.delete(cacheKey); setSubject(''); setBody(''); setPreview(null); refresh()
      // The send moves the conversation (to Waiting, first review recorded): refresh the desk's queue, counts and
      // menu badges. Nothing outside the desk uses the ['support'] keys, so the legacy pages are unaffected.
      qc.invalidateQueries({ queryKey: ['support'] })
    },
    onError: (err) => {
      // A stale draft means the thread moved under us: show the message and reload it.
      if (err instanceof ApiError && err.status === 409 && !isRepeatConflict(err)) {
        qc.invalidateQueries({ queryKey: key })
      }
    },
  })

  if (thread.isLoading) return <p className="text-paper-trace text-sm">Loading reply…</p>
  if (thread.isError || !thread.data) return <p className="text-red-400 text-sm">Couldn't load the reply. Try again.</p>
  if (!thread.data.can_reply) {
    const none = <p className="text-paper-trace text-sm">No email address to reply to.</p>
    if (!extraTools) return none
    return <div className="space-y-2">{none}<InsertContext.Provider value={NO_ADDRESS}>{extraTools}</InsertContext.Provider></div>
  }

  const kept = actions.filter((a) => a.status === 'proposed' || a.status === 'failed')
    .filter((a) => !choices[a.index]?.skip).map((a) => a.index)
  const markerIssues = markerProblems(body, kept)
  const baseGate = canSend(usable?.status === 'ready' ? usable : null, subject, body)
  const gate = baseGate.ok && markerIssues.length ? { ok: false, reason: markerIssues[0] } : baseGate
  const repeat = isRepeatConflict(send.error)
  const applyErr = applyDetail(send.error)
  const sendWithSkip = (index: number) => {
    setChoices((c) => ({ ...c, [index]: { ...(c[index] ?? { index, staff_verified: false }), index, skip: true } }))
    send.reset()
  }

  return (
    <section className="space-y-3 border-t border-ink-500 pt-3" aria-label="Reply">
      {timeline(thread.data).map((e) => e.who === 'us' ? (
        <div key={e.key} className="border-l-2 border-amber-glow pl-3">
          <p className="text-xs text-paper-trace">
            Replied by {e.reply!.sent_by}{e.at ? ` · ${fmtRelative(e.at)}` : ''}
            {e.reply!.from_editorial_desk ? ' · from the Editorial Desk'
              : e.reply!.edit_ratio !== null ? ` · ${Math.round(e.reply!.edit_ratio * 100)}% changed from the draft` : ' · written by hand'}
          </p>
          <p className="text-paper-dim whitespace-pre-wrap text-sm">{e.text}</p>
        </div>
      ) : (
        <InboundBlock key={e.key} item={e.inbound!} />
      ))}

      {draft?.status === 'drafting' && <p className="label">Drafting a reply… this takes up to 3 minutes.</p>}
      {draft?.status === 'failed' && (
        <p className="text-red-400 text-sm">Draft unavailable: {draft.error}</p>
      )}
      {draft?.status === 'no_reply_needed' && (
        <p className="text-paper-dim text-sm">No reply needed: {draft.body_md}</p>
      )}

      {usable?.status === 'ready' && (
        <div className="text-sm text-paper-dim flex flex-wrap gap-3">
          <span className="label">{usable.category}</span>
          <span className="label">confidence {usable.confidence}</span>
        </div>
      )}
      {usable?.promise_warnings.length ? (
        <div className="border border-red-400 p-2 text-sm text-red-300">
          <p className="label mb-1">Promises not on the staff-action list</p>
          <ul className="list-disc pl-5">{usable.promise_warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      ) : null}

      {usable?.status === 'ready' && usable.staff_actions.length > 0 && (
        <div className="space-y-1">
          <p className="label">Staff actions</p>
          {usable.staff_actions.map((a, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-paper">{a.text}</span>
              {a.resolution ? (
                <span className="label">{a.resolution === 'done' ? 'Done' : 'Not needed'} · {a.resolved_by}</span>
              ) : (
                <>
                  <button type="button" className="label-amber hover:underline"
                    onClick={() => resolve.mutate({ index: i, resolution: 'done' })}>Done</button>
                  <button type="button" className="label hover:underline"
                    onClick={() => resolve.mutate({ index: i, resolution: 'not_needed' })}>Not needed</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {usable?.status === 'ready' && usable.unverified.length > 0 && (
        <div className="text-sm">
          <p className="label">Not verified, check these first</p>
          <ul className="list-disc pl-5 text-amber-glow">{usable.unverified.map((u, i) => <li key={i}>{u}</li>)}</ul>
        </div>
      )}

      {usable?.status === 'ready' && (
        <ReplyActionCards actions={actions} choices={choices} onChange={(i, c, t) => {
          setChoices((prev) => ({ ...prev, [i]: c }))
          if (t) setExpected((prev) => ({ ...prev, [i]: t }))
        }} />
      )}

      <label className="block">
        <span className="label">Subject</span>
        <input value={subject} onChange={(e) => edit(e.target.value, body)}
          className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2" />
      </label>
      <label className="block">
        <span className="label">
          Reply to {thread.data.message.email}, sent as {thread.data.message.publication.replace(/_/g, ' ')}
        </span>
        <textarea ref={bodyRef} rows={12} value={body} onChange={(e) => edit(subject, e.target.value)}
          onSelect={(e) => {
            selection.current = { start: e.currentTarget.selectionStart, end: e.currentTarget.selectionEnd, draft: loadedDraft }
          }}
          className="mt-1 w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
      </label>
      <p className="text-xs text-paper-trace">
        **bold**, "- " bullets, "1. " steps, [link](https://…), one [button: Label](https://…).
        The sign-off and their message are added below your reply.
        {actions.length > 0 && ' Each {{action:N}} becomes the sentence for what actually happened.'}
      </p>
      {markerIssues.map((m) => <p key={m} className="text-xs text-amber-glow">{m}</p>)}

      <div className="flex flex-wrap gap-3 items-center">
        <button type="button" className="label-amber hover:underline disabled:opacity-50"
          disabled={!gate.ok || send.isPending} onClick={() => send.mutate(false)}>
          {send.isPending ? 'Sending…' : (actions.length && usable?.status === 'ready' ? 'Apply and send' : 'Send')}
        </button>
        <button type="button" className="label hover:underline" disabled={!body.trim()}
          onClick={() => showPreview.mutate()}>Preview</button>
        <button type="button" className="label hover:underline" disabled={redraft.isPending}
          onClick={() => redraft.mutate()}>Redraft</button>
        {extraTools && (
          <InsertContext.Provider value={{ ready: true, insertIntoBody }}>{extraTools}</InsertContext.Provider>
        )}
        <input value={instruction} onChange={(e) => setInstruction(e.target.value)}
          placeholder="Optional: what to change in the redraft"
          className="bg-ink-900 border border-ink-600 text-paper text-[13px] px-2 py-1 min-w-[260px]" />
        {usable && (
          <button type="button" className="label hover:underline"
            onClick={() => {
              const reason = window.prompt('Why discard this draft? (optional)')
              if (reason !== null) discard.mutate(reason)
            }}>
            Discard draft
          </button>
        )}
      </div>
      {!gate.ok && <p className="text-xs text-paper-trace">{gate.reason}</p>}
      {redraft.isError && <p className="text-red-400 text-sm">{errText(redraft.error)}</p>}
      {discard.isError && <p className="text-red-400 text-sm">{errText(discard.error)}</p>}
      {resolve.isError && <p className="text-red-400 text-sm">{errText(resolve.error)}</p>}
      {showPreview.isError && <p className="text-red-400 text-sm">{errText(showPreview.error)}</p>}
      {send.isError && applyErr?.reasons && (
        <div className="text-red-400 text-sm">
          <p>{applyErr.message}</p>
          <ul className="list-disc pl-5">{applyErr.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
      )}
      {send.isError && applyErr?.index !== undefined && (
        <p className="text-red-400 text-sm">
          Action {applyErr.index} failed: {applyErr.reason}. The reply wasn't sent.{' '}
          <button type="button" className="label-amber hover:underline"
            onClick={() => sendWithSkip(applyErr.index as number)}>Skip it</button>{' '}
          <button type="button" className="label hover:underline" onClick={() => send.mutate(false)}>Retry</button>
        </p>
      )}
      {send.isError && !applyErr && (
        <p className="text-red-400 text-sm">
          {errText(send.error, true)}{' '}
          {repeat && <button type="button" className="label-amber hover:underline"
            onClick={() => send.mutate(true)}>Send anyway</button>}
        </p>
      )}

      {preview && (
        <div className="space-y-2">
          <div className="flex gap-3">
            <button type="button" className={width === 393 ? 'label-amber' : 'label'} onClick={() => setWidth(393)}>Phone</button>
            <button type="button" className={width === 760 ? 'label-amber' : 'label'} onClick={() => setWidth(760)}>Desktop</button>
          </div>
          <iframe title="Email preview" sandbox="" srcDoc={preview} style={{ width, height: 900 }}
            className="border border-ink-500 bg-white max-w-full" />
        </div>
      )}

      {usable && usable.checks.length > 0 && (
        <div>
          <button type="button" className="label hover:underline" aria-expanded={showChecks}
            onClick={() => setShowChecks(!showChecks)}>
            What I checked ({usable.checks.length})
          </button>
          {showChecks && (
            <div className="mt-2 space-y-2 text-xs">
              <ul className="space-y-1">
                {usable.facts.map((f, i) => (
                  <li key={i} className="text-paper-dim">{f.text} <span className="label">{f.check_id}</span></li>
                ))}
              </ul>
              {usable.checks.map((c) => (
                <details key={c.id} className="border border-ink-600 p-2">
                  <summary className={c.refused ? 'text-red-300' : 'text-paper'}>
                    {c.id} · {c.tool} {JSON.stringify(c.input)}
                  </summary>
                  <pre className="whitespace-pre-wrap break-all text-paper-trace">{JSON.stringify(c.result, null, 1)}</pre>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
      {usable?.context_pruned_at && <p className="text-xs text-paper-trace">Checks cleared after 90 days.</p>}
    </section>
  )
}

/** Shown while the replies flag is unknown; a failed probe offers a retry
 *  instead of "Checking…" forever. */
export function ReplySettingsPending({ query }: { query: { isError: boolean; refetch: () => unknown } }) {
  if (!query.isError) return <p className="text-paper-trace text-sm">Checking reply settings…</p>
  return (
    <p className="text-red-400 text-sm">
      Couldn't check reply settings.{' '}
      <button type="button" className="label hover:underline" onClick={() => query.refetch()}>
        Try again
      </button>
    </p>
  )
}
